import React, { useState, useEffect } from "react";
import { Formik, ErrorMessage, Field } from "formik";
import Loading from "../components/loading";
import * as Yup from "yup";
import axiosInstance from "../../../../services/AxiosInstance";
import swal from "sweetalert";
import DatePicker from "react-datepicker";
import { useNavigate, useParams } from "react-router-dom";
import { BotonPrimaryText } from "../components/BotonesCustom";
import moment from "moment";
import { ButtonGroup } from "react-bootstrap";
import QuillEditorBasic from "../components/QuillEditorBasic";

import "react-datepicker/dist/react-datepicker.css";
import "react-quill/dist/quill.snow.css";

const DIAS_SEMANA = [
  { value: 0, label: "Lunes" },
  { value: 1, label: "Martes" },
  { value: 2, label: "Miércoles" },
  { value: 3, label: "Jueves" },
  { value: 4, label: "Viernes" },
  { value: 5, label: "Sábado" },
  { value: 6, label: "Domingo" },
];

const formZonaSocialSchema = Yup.object().shape({
  nombre: Yup.string().required("Por favor, ingresa un nombre.").nullable(true),
  responsable: Yup.string()
    .required("Por favor, ingresa un responsable.")
    .nullable(true),
  horario_inicio: Yup.date()
    .required("Por favor, ingresa un horario de inicio.")
    .nullable(true),
  horario_fin: Yup.date()
    .required("Por favor, ingresa un horario de fin.")
    .nullable(true),
  capacidad_min: Yup.number()
    .required("Por favor, ingresa una capacidad mínima.")
    .min(1, "Debe ser mayor o igual a 1.")
    .max(100, "Debe ser menor o igual a 100.")
    .nullable(true),
  capacidad_max: Yup.number()
    .required("Por favor, ingresa una capacidad máxima.")
    .min(1, "Debe ser mayor o igual a 1.")
    .max(100, "Debe ser menor o igual a 100.")
    .nullable(true),
  reservable: Yup.boolean().nullable(true),
  requiere_aprobacion_manual: Yup.boolean().nullable(true),
  tipo_reserva: Yup.string()
    .required("Por favor, selecciona un tipo de reserva.")
    .oneOf(["hora", "dia"], "Tipo de reserva inválido."),
  tiempo_maximo_reserva: Yup.number()
    .min(1, "Debe ser al menos 1.")
    .max(24, "No puede exceder 24.")
    .nullable(true),
  dias_operacion: Yup.array()
    .min(1, "Debes seleccionar al menos un día de operación.")
    .required("Debes seleccionar al menos un día de operación."),
  max_reservas_por_vivienda_mes: Yup.number()
    .min(0, "Debe ser mayor o igual a 0.")
    .nullable(true),
  reglas_uso: Yup.string()
    .max(2000, "Las reglas de uso no pueden exceder 2000 caracteres.")
    .nullable(true),
  imagen: Yup.mixed()
    .nullable()
    .test(
      "fileSize",
      "La imagen no puede exceder 5MB",
      (value) => !value || (value && value.size <= 5 * 1024 * 1024)
    )
    .test(
      "fileType",
      "Solo se permiten imágenes (JPEG, PNG, WEBP, GIF)",
      (value) =>
        !value ||
        (value &&
          ["image/jpeg", "image/png", "image/webp", "image/gif"].includes(
            value.type
          ))
    ),
});

export const FormEditarZonaSocial = () => {
  const params = useParams();
  const URL = `/api/v3/zonas-sociales/zonas/${params.uuid}/`;

  const initialValuesEmpty = {
    nombre: "",
    responsable: "",
    valor: 0,
    reservable: true,
    requiere_aprobacion_manual: true,
    tipo_reserva: "hora",
    horario_inicio: null,
    horario_fin: null,
    dias_operacion: [0, 1, 2, 3, 4, 5, 6],
    capacidad_min: 1,
    capacidad_max: 10,
    max_reservas_por_vivienda_mes: 0,
    tiempo_maximo_reserva: 2,
    imagen: null,
    imagen_url: null,
    reglas_uso: "",
  };

  const [loading, setLoading] = useState(true);
  const [loadingBtn, setLoadingBtn] = useState(false);
  const [imagenPreview, setImagenPreview] = useState(null);
  const [imagenEliminada, setImagenEliminada] = useState(false);
  const [initialValues, setInitialValues] = useState(initialValuesEmpty);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchZonaSocial = async () => {
      setLoading(true);
      try {
        const response = await axiosInstance.get(URL);
        const data = response.data;

        let horario_inicio = data.horario_inicio;
        if (data.horario_inicio !== null) {
          horario_inicio = moment(data.horario_inicio, "HH:mm:ss").toDate();
        }
        let horario_fin = data.horario_fin;
        if (data.horario_fin !== null) {
          horario_fin = moment(data.horario_fin, "HH:mm:ss").toDate();
        }

        setInitialValues({
          ...data,
          horario_fin: horario_fin,
          horario_inicio: horario_inicio,
          dias_operacion: data.dias_operacion || [0, 1, 2, 3, 4, 5, 6],
          tipo_reserva: data.tipo_reserva || "hora",
          tiempo_maximo_reserva: data.tiempo_maximo_reserva || 2,
          reglas_uso: data.reglas_uso || "",
          imagen: null,
          imagen_url: data.imagen_url || data.imagen || null,
        });
        if (data.imagen_url || data.imagen) {
          setImagenPreview(data.imagen_url || data.imagen);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        swal("Error", "No se pudo cargar la zona social", "error");
        navigate("/zonas-sociales");
      } finally {
        setLoading(false);
      }
    };

    fetchZonaSocial();
  }, [URL, navigate]);

  const handleSubmit = async (
    values,
    { setSubmitting, resetForm, setFieldError }
  ) => {
    try {
      setLoadingBtn(true);
      const formattedStartDate = values.horario_inicio
        ? values.horario_inicio.toLocaleTimeString("es-CO", { hour12: false })
        : null;
      const formattedEndDate = values.horario_fin
        ? values.horario_fin.toLocaleTimeString("es-CO", { hour12: false })
        : null;

      const formData = new FormData();
      formData.append("nombre", values.nombre);
      formData.append("responsable", values.responsable);
      formData.append("valor", values.valor);
      formData.append("reservable", values.reservable);
      formData.append("requiere_aprobacion_manual", values.requiere_aprobacion_manual);
      formData.append("tipo_reserva", values.tipo_reserva);
      formData.append("horario_inicio", formattedStartDate);
      formData.append("horario_fin", formattedEndDate);
      formData.append("dias_operacion", JSON.stringify(values.dias_operacion));
      formData.append("capacidad_min", values.capacidad_min);
      formData.append("capacidad_max", values.capacidad_max);
      formData.append("max_reservas_por_vivienda_mes", values.max_reservas_por_vivienda_mes);
      formData.append("tiempo_maximo_reserva", values.tiempo_maximo_reserva);
      formData.append("reglas_uso", values.reglas_uso || "");

      if (values.imagen) {
        formData.append("imagen", values.imagen);
      } else if (imagenEliminada) {
        formData.append("imagen", "");
      }

      const response = await axiosInstance.patch(URL, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      navigate("/zonas-sociales");
    } catch (error) {
      if (error.response && error.response.data) {
        const apiErrors = error.response.data;
        Object.keys(apiErrors).forEach((fieldName) => {
          if (fieldName === "non_field_errors") {
            let error_mensaje = "";
            apiErrors[fieldName].forEach((error) => {
              error_mensaje = `${error_mensaje} ${error}`;
            });
            swal("Error al actualizar zona social", error_mensaje, "error");
          } else {
            const formattedErrors = apiErrors[fieldName].map((error, index) => (
              <React.Fragment key={index}>
                {error}
                {index < apiErrors[fieldName].length - 1 && <br />}
              </React.Fragment>
            ));
            setFieldError(fieldName, formattedErrors);
          }
        });
      }
      console.error("Error fetching data:", error);
      swal(
        "Error",
        "Ha ocurrido un error al enviar el formulario. Por favor, intenta nuevamente.",
        "error"
      );
    } finally {
      setLoadingBtn(false);
    }
  };

  const handleImagenChange = (event, setFieldValue) => {
    const file = event.currentTarget.files[0];
    if (file) {
      setFieldValue("imagen", file);
      setImagenEliminada(false);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagenPreview(reader.result);
      };
      reader.readAsDataURL(file);
    } else {
      setFieldValue("imagen", null);
    }
  };

  const handleEliminarImagen = (setFieldValue) => {
    setFieldValue("imagen", null);
    setFieldValue("imagen_url", null);
    setImagenPreview(null);
    setImagenEliminada(true);
  };

  const handleDiaChange = (dia, values, setFieldValue) => {
    const diasActuales = values.dias_operacion || [];
    const nuevosDias = diasActuales.includes(dia)
      ? diasActuales.filter((d) => d !== dia)
      : [...diasActuales, dia];
    setFieldValue("dias_operacion", nuevosDias);
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <>
      <div className="d-flex align-items-center mb-4 flex-wrap">
        <h4 className="fs-20 font-w600 me-auto">Editar Zona Social</h4>
      </div>
      <div className="card">
        <Formik
          initialValues={initialValues}
          validationSchema={formZonaSocialSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
            isSubmitting,
            setFieldValue,
          }) => (
            <form onSubmit={handleSubmit}>
              <div className="card-body">
                <div className="row g-3">
                  <div className="col-md-6">
                    <div className="form-group">
                      <label htmlFor="nombre">
                        Nombre <span className="text-danger">*</span>
                      </label>
                      <Field
                        name="nombre"
                        id="nombre"
                        className={`form-control ${
                          errors.nombre && touched.nombre ? "is-invalid" : ""
                        }`}
                      />
                      <ErrorMessage
                        name="nombre"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="form-group">
                      <label htmlFor="responsable">
                        Responsable <span className="text-danger">*</span>
                      </label>
                      <Field
                        name="responsable"
                        id="responsable"
                        className={`form-control ${
                          errors.responsable && touched.responsable
                            ? "is-invalid"
                            : ""
                        }`}
                      />
                      <ErrorMessage
                        name="responsable"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>
                  </div>

                  <div className="col-md-4">
                    <div className="form-group">
                      <label htmlFor="valor">Valor</label>
                      <Field
                        type="number"
                        name="valor"
                        id="valor"
                        className="form-control"
                      />
                      <small className="text-muted">
                        Si ingresas un valor mayor a 0, la zona se marcará automáticamente como de pago.
                      </small>
                    </div>
                  </div>

                  <div className="col-md-4">
                    <div className="form-group">
                      <label htmlFor="reservable" className="d-block">
                        Reservable
                      </label>
                      <Field
                        type="checkbox"
                        name="reservable"
                        id="reservable"
                        checked={values.reservable}
                        className="form-check-input"
                      />
                    </div>
                  </div>

                  <div className="col-md-4">
                    <div className="form-group">
                      <label
                        htmlFor="requiere_aprobacion_manual"
                        className="d-block"
                      >
                        Requiere aprobación manual
                      </label>
                      <Field
                        type="checkbox"
                        name="requiere_aprobacion_manual"
                        id="requiere_aprobacion_manual"
                        checked={values.requiere_aprobacion_manual}
                        className="form-check-input"
                      />
                    </div>
                  </div>

                  <div className="col-12">
                    <hr />
                    <h5 className="font-w600">Configuración de Reservas</h5>
                  </div>

                  <div className="col-md-6">
                    <div className="form-group">
                      <label htmlFor="tipo_reserva">
                        Tipo de Reserva <span className="text-danger">*</span>
                      </label>
                      <div className="mt-2">
                        <div className="form-check form-check-inline">
                          <Field
                            type="radio"
                            name="tipo_reserva"
                            id="tipo_reserva_hora"
                            value="hora"
                            className="form-check-input"
                          />
                          <label
                            className="form-check-label"
                            htmlFor="tipo_reserva_hora"
                          >
                            Por Hora
                          </label>
                        </div>
                        <div className="form-check form-check-inline">
                          <Field
                            type="radio"
                            name="tipo_reserva"
                            id="tipo_reserva_dia"
                            value="dia"
                            className="form-check-input"
                          />
                          <label
                            className="form-check-label"
                            htmlFor="tipo_reserva_dia"
                          >
                            Por Día Completo
                          </label>
                        </div>
                      </div>
                      <ErrorMessage
                        name="tipo_reserva"
                        component="div"
                        className="text-danger small"
                      />
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="form-group">
                      <label htmlFor="tiempo_maximo_reserva">
                        {values.tipo_reserva === "hora"
                          ? "Máximo de Horas"
                          : "Duración"}
                      </label>
                      {values.tipo_reserva === "hora" ? (
                        <>
                          <Field
                            type="number"
                            name="tiempo_maximo_reserva"
                            id="tiempo_maximo_reserva"
                            className={`form-control ${
                              errors.tiempo_maximo_reserva &&
                              touched.tiempo_maximo_reserva
                                ? "is-invalid"
                                : ""
                            }`}
                            min="1"
                            max="24"
                          />
                          <small className="text-muted">
                            Cantidad máxima de horas consecutivas que se pueden
                            reservar
                          </small>
                          <ErrorMessage
                            name="tiempo_maximo_reserva"
                            component="div"
                            className="invalid-feedback"
                          />
                        </>
                      ) : (
                        <div className="form-control-plaintext">
                          <span className="badge bg-info">1 día completo</span>
                          <small className="text-muted ms-2">
                            La reserva será por todo el día
                          </small>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="form-group">
                      <label htmlFor="horario_inicio">
                        Horario de Atención{" "}
                        <span className="text-danger">*</span>
                      </label>
                      <div className="d-flex align-items-center gap-2">
                        <DatePicker
                          selected={values.horario_inicio}
                          name="horario_inicio"
                          id="horario_inicio"
                          className={`form-control ${
                            errors.horario_inicio && touched.horario_inicio
                              ? "is-invalid"
                              : ""
                          }`}
                          showTimeSelect
                          showTimeSelectOnly
                          dateFormat="HH:mm"
                          onChange={(date) =>
                            setFieldValue("horario_inicio", date)
                          }
                          placeholderText="Inicio"
                        />
                        <span>a</span>
                        <DatePicker
                          selected={values.horario_fin}
                          name="horario_fin"
                          id="horario_fin"
                          className={`form-control ${
                            errors.horario_fin && touched.horario_fin
                              ? "is-invalid"
                              : ""
                          }`}
                          showTimeSelect
                          showTimeSelectOnly
                          dateFormat="HH:mm"
                          onChange={(date) =>
                            setFieldValue("horario_fin", date)
                          }
                          placeholderText="Fin"
                        />
                      </div>
                      <ErrorMessage
                        name="horario_inicio"
                        component="div"
                        className="text-danger small"
                      />
                      <ErrorMessage
                        name="horario_fin"
                        component="div"
                        className="text-danger small"
                      />
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="form-group">
                      <label>
                        Días de Operación{" "}
                        <span className="text-danger">*</span>
                      </label>
                      <div className="d-flex flex-wrap gap-2 mt-2">
                        {DIAS_SEMANA.map((dia) => (
                          <div key={dia.value} className="form-check">
                            <input
                              type="checkbox"
                              id={`dia_${dia.value}`}
                              className="form-check-input"
                              checked={values.dias_operacion?.includes(
                                dia.value
                              )}
                              onChange={() =>
                                handleDiaChange(
                                  dia.value,
                                  values,
                                  setFieldValue
                                )
                              }
                            />
                            <label
                              className="form-check-label"
                              htmlFor={`dia_${dia.value}`}
                            >
                              {dia.label}
                            </label>
                          </div>
                        ))}
                      </div>
                      <ErrorMessage
                        name="dias_operacion"
                        component="div"
                        className="text-danger small"
                      />
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="form-group">
                      <label>
                        Capacidad <span className="text-danger">*</span>
                      </label>
                      <div className="d-flex align-items-center gap-2">
                        <Field
                          type="number"
                          name="capacidad_min"
                          id="capacidad_min"
                          className={`form-control ${
                            errors.capacidad_min && touched.capacidad_min
                              ? "is-invalid"
                              : ""
                          }`}
                          min="1"
                          placeholder="Mín"
                        />
                        <span>a</span>
                        <Field
                          type="number"
                          name="capacidad_max"
                          id="capacidad_max"
                          className={`form-control ${
                            errors.capacidad_max && touched.capacidad_max
                              ? "is-invalid"
                              : ""
                          }`}
                          min="1"
                          placeholder="Máx"
                        />
                      </div>
                      <ErrorMessage
                        name="capacidad_min"
                        component="div"
                        className="text-danger small"
                      />
                      <ErrorMessage
                        name="capacidad_max"
                        component="div"
                        className="text-danger small"
                      />
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="form-group">
                      <label htmlFor="max_reservas_por_vivienda_mes">
                        Máx. reservas por vivienda/mes
                      </label>
                      <Field
                        type="number"
                        name="max_reservas_por_vivienda_mes"
                        id="max_reservas_por_vivienda_mes"
                        className="form-control"
                        min="0"
                      />
                      <small className="text-muted">
                        0 significa ilimitado
                      </small>
                    </div>
                  </div>

                  <div className="col-12">
                    <hr />
                    <h5 className="font-w600">Imagen y Reglas</h5>
                  </div>

                  <div className="col-md-6">
                    <div className="form-group">
                      <label htmlFor="imagen">Imagen de la Zona</label>
                      <input
                        type="file"
                        name="imagen"
                        id="imagen"
                        className={`form-control ${
                          errors.imagen && touched.imagen ? "is-invalid" : ""
                        }`}
                        accept="image/jpeg,image/png,image/webp,image/gif"
                        onChange={(event) =>
                          handleImagenChange(event, setFieldValue)
                        }
                      />
                      <small className="text-muted">
                        Formatos: JPEG, PNG, WEBP, GIF. Máximo 5MB.
                      </small>
                      <ErrorMessage
                        name="imagen"
                        component="div"
                        className="text-danger small"
                      />
                      {imagenPreview && (
                        <div className="mt-2 d-flex align-items-center gap-2">
                          <img
                            src={imagenPreview}
                            alt="Preview"
                            className="img-thumbnail"
                            style={{
                              maxHeight: "100px",
                              objectFit: "cover",
                            }}
                          />
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => handleEliminarImagen(setFieldValue)}
                          >
                            <i className="fas fa-trash"></i> Eliminar
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="form-group">
                      <label htmlFor="reglas_uso">Reglas de Uso</label>
                      <QuillEditorBasic
                        value={values.reglas_uso || ""}
                        onChange={(content) => setFieldValue("reglas_uso", content)}
                        placeholder="Ej: No se permiten mascotas, horario de silencio después de las 10pm..."
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="card-footer d-flex justify-content-end">
                <ButtonGroup>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => navigate("/zonas-sociales")}
                  >
                    Cancelar
                  </button>
                  <BotonPrimaryText type="submit" disabled={isSubmitting || loadingBtn}>
                    {loadingBtn ? (
                      <>
                        <i className="fas fa-circle-notch fa-spin me-2"></i>
                        Guardando...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-save me-2"></i>
                        Actualizar
                      </>
                    )}
                  </BotonPrimaryText>
                </ButtonGroup>
              </div>
            </form>
          )}
        </Formik>
      </div>
    </>
  );
};

export default FormEditarZonaSocial;
